"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CreateOrEditContent = void 0;
var _zod = require("@hookform/resolvers/zod");
var _components = require("@ori-ui/components");
var _mui = require("@ori-ui/mui");
var _auth = require("@ori/auth");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _reactHookForm = require("react-hook-form");
var _api = require("../../api");
var _createProductList = require("../../api/createProductList");
var _constants = require("../../constants");
var _favoritesContext = require("../../contexts/favoritesContext");
var _hooks = require("../../hooks");
var _utils = require("../../utils");
var _FavoritesDialogContent = require("../FavoritesDialogContent/FavoritesDialogContent");
var _constants2 = require("./constants");
var _styles = require("./styles");
var _utils2 = require("./utils");
var _jsxRuntime = require("react/jsx-runtime");
const CreateOrEditContent = ({
  isCreateMode,
  lastUsedListId,
  prefilledProductCodes,
  shouldRedirectOnSuccess,
  onDialogClose
}) => {
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const {
    customerId
  } = (0, _auth.useToken)();
  const globalApiClient = (0, _globalApiClientReact.useApiClient)();
  const {
    translations,
    formatMessage
  } = (0, _hooks.useTranslations)();
  const {
    getList,
    openSnackbar,
    updateLists
  } = (0, _favoritesContext.useFavorites)();
  const list = (0, _react.useMemo)(() => getList(lastUsedListId), [getList, lastUsedListId]);
  const form = (0, _reactHookForm.useForm)({
    defaultValues: {
      description: list?.description,
      name: list?.name
    },
    mode: 'all',
    resolver: (0, _zod.zodResolver)((0, _utils2.getCreateOrEditListSchema)(formatMessage))
  });
  const submitDisabled = (0, _react.useMemo)(() => form.formState.isSubmitting || !form.formState.isValid || !form.formState.isDirty, [form.formState.isDirty, form.formState.isSubmitting, form.formState.isValid]);
  const onSubmit = (0, _react.useCallback)(async ({
    name,
    description
  }) => {
    try {
      if (isCreateMode) {
        const createdList = await (0, _createProductList.createProductList)({
          client: globalApiClient,
          customerId,
          description,
          name,
          productCodes: prefilledProductCodes,
          type: 'Personal'
        });
        if (createdList) {
          openSnackbar(translations.createSuccess);
          // istanbul ignore next -- We are not testing the else case
          updateLists(prevLists => (0, _utils.updateListInLists)({
            list: createdList,
            lists: prevLists
          }));
          if (shouldRedirectOnSuccess) {
            window.location.href = `${_constants.FAVORITE_LISTS_LANDING_PAGE}${createdList.id}`;
          }
        }
      } else if (list) {
        const updatedList = await (0, _api.updateProductList)({
          client: globalApiClient,
          customerId,
          id: list.id,
          productCodes: list.productCodes,
          type: 'Personal',
          description,
          name
        });
        if (updatedList) {
          openSnackbar(translations.editSuccess);
          // istanbul ignore next -- We are not testing the else case
          updateLists(prevLists => (0, _utils.updateListInLists)({
            list: updatedList,
            lists: prevLists
          }));
          if (shouldRedirectOnSuccess) {
            window.location.href = `${_constants.FAVORITE_LISTS_LANDING_PAGE}${updatedList.id}`;
          }
        }
      }
    } catch (error) {
      openSnackbar(isCreateMode ? translations.createFailed : translations.editFailed);
    } finally {
      onDialogClose();
    }
  }, [customerId, globalApiClient, isCreateMode, list, onDialogClose, openSnackbar, prefilledProductCodes, shouldRedirectOnSuccess, translations.createFailed, translations.createSuccess, translations.editFailed, translations.editSuccess, updateLists]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_FavoritesDialogContent.FavoritesDialogContent, {
    testId: getTestId(_constants2.CREATE_OR_EDIT_CONTENT),
    message: isCreateMode ? translations.createDescription : translations.editDescription,
    content: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactHookForm.FormProvider, {
      ...form,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("form", {
        onSubmit: form.handleSubmit(onSubmit),
        children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.Root, {
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.NameField, {
            ...form.register('name'),
            required: true,
            error: Boolean(form.formState.errors.name),
            helperText: form.formState.errors.name?.message,
            label: translations.listName,
            margin: "none",
            placeholder: translations.listName,
            slotProps: {
              htmlInput: {
                [_constants2.DATA_TESTID]: getTestId(_constants2.CREATE_OR_EDIT_CONTENT_NAME)
              }
            }
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.TextField, {
            ...form.register('description'),
            multiline: true,
            error: Boolean(form.formState.errors.description),
            helperText: form.formState.errors.description?.message,
            label: translations.description,
            margin: "none",
            minRows: 4,
            placeholder: formatMessage('descriptionPlaceholder', {
              maxLength: _constants2.LIST_DESCRIPTION_MAX_LENGTH
            }),
            slotProps: {
              htmlInput: {
                [_constants2.DATA_TESTID]: getTestId(_constants2.CREATE_OR_EDIT_CONTENT_DESCRIPTION)
              }
            }
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.Buttons, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
              fullWidth: true,
              "data-testid": getTestId(_constants2.CREATE_OR_EDIT_CONTENT_SUBMIT),
              type: "submit",
              variant: "contained",
              color: "primary",
              disabled: submitDisabled,
              children: translations.saveList
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
              fullWidth: true,
              variant: "text",
              onClick: onDialogClose,
              children: translations.cancel
            })]
          })]
        })
      })
    })
  });
};
exports.CreateOrEditContent = CreateOrEditContent;
//# sourceMappingURL=CreateOrEditContent.js.map